import { createSelector } from '@reduxjs/toolkit';

import { RegionToAudience } from '@/constants';
import { PlanSegment, PurchaseType } from '@/types';

import { selectAppRegion } from '../app-settings';
import { playerSessionsSelectors } from '../player-fsm-api';
import { RootState } from '../store';
import { userApiPostgrest } from './user-api.slice';

export const selectUserProfileResult =
  userApiPostgrest.endpoints.fetchUserProfile.select(undefined);

export const selectIsMemberAdmin = createSelector(
  selectUserProfileResult,
  ({ data: userProfile }) =>
    userProfile?.memberRoles?.some(r => r.roleId === 'member_admin'),
);

export const selectIsFreeTrialUser = (state: RootState) =>
  selectPlan(state)?.data?.purchaseType === PurchaseType.FREE_TRIAL;

export const selectUserRegion = createSelector(
  [userApiPostgrest.endpoints.fetchPrimaryRegion.select(), selectAppRegion],
  ({ data: primaryRegion }, appRegion) => primaryRegion || appRegion,
);

export const selectUserAudienceRegions = createSelector(
  [
    userApiPostgrest.endpoints.fetchContentPreferences.select(),
    userApiPostgrest.endpoints.fetchPrimaryRegion.select(),
    selectAppRegion,
  ],
  ({ data: contentPreferences }, { data: primaryRegion }, appRegion) =>
    contentPreferences?.audienceRegions ?? [
      RegionToAudience[primaryRegion ?? appRegion],
    ],
);

export const selectPlan = userApiPostgrest.endpoints.fetchPlan.select();

export const selectIsB2CUser = createSelector(
  selectPlan,
  ({ data: plan }) => !plan || plan.segment === PlanSegment.B2C,
);

export const selectIsAdminUser = createSelector(
  selectUserProfileResult,
  ({ data: profile }) =>
    profile?.memberRoles?.some(({ roleId }) => roleId === 'member_admin'),
);

export const selectUserNeedsPlanUpgrade = createSelector(
  selectPlan,
  ({ data: plan }) =>
    plan &&
    plan.segment === PlanSegment.B2C &&
    plan.purchaseType !== PurchaseType.FREE_TRIAL &&
    !plan.licenceValid,
);

// If user has completed an episode but not set up a recovery email we can prompt them
export const selectRecoveryEmailPending = createSelector(
  // Don't reference selectors directly or it won't load correctly
  (state: RootState) => playerSessionsSelectors.selectAll(state),
  userApiPostgrest.endpoints.fetchRecoveryEmail.select(),
  (sessions, { data: recoveryEmail }) =>
    sessions.some(({ chapterStates }) =>
      chapterStates.some(state => state !== 'ready'),
    ) &&
    (recoveryEmail?.verificationStatus === 'unverified' ||
      recoveryEmail?.verificationStatus === 'not_attempted'),
);
